<template>
  <div v-if="!forbidden" class="accruals-block mt-6">
    <div class="d-flex align-center mb-2">
      <h3 class="accruals-block__title">Начисления</h3>
      <v-tooltip bottom max-width="320">
        <template v-slot:activator="{ on, attrs }">
          <v-icon small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
        </template>
        <span>Поступления тестов в буфер компании: заказы из каталога и ручные начисления.</span>
      </v-tooltip>
      <v-spacer />
      <a v-if="canOpenOperations" class="text-primary caption" @click="$emit('open-tab', 'operations')">Вся история →</a>
    </div>

    <StateBlock v-if="loading && !items.length" state="loading" compact />
    <StateBlock v-else-if="error" state="error" compact text="Не удалось загрузить начисления." @retry="load" />
    <div v-else-if="!items.length" class="caption grey--text">
      {{ limitReached ? 'Среди последних операций начислений не найдено. Полный список — в «Истории операций».' : 'Начислений пока не было.' }}
    </div>
    <v-simple-table v-else dense>
      <thead>
        <tr>
          <th>Дата</th><th>Тест</th><th class="text-right">Кол-во</th><th>Источник</th><th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, ind) in items" :key="row.id">
          <td>{{ formatDate(row.date) }}</td>
          <td>{{ row.testName }}</td>
          <td class="text-right success--text">+{{ row.qty }}</td>
          <td>{{ row.sourceLabel || '—' }}</td>
          <td><v-chip v-if="ind === 0" x-small color="primary" outlined>Последнее</v-chip></td>
        </tr>
      </tbody>
    </v-simple-table>
  </div>
</template>

<script>
import moment from 'moment';
import api from '@/common/api';
import StateBlock from '@/components/ui/StateBlock.vue';
import { parseWalletTransactions } from '@/common/models/walletTransactions';

const FORMAT = 'YYYY-MM-DD';
const SHOW = 5;
const PAGE_SIZE = 200;
// Сервер не умеет фильтровать «приход в буфер», поэтому отбираем на экране и листаем журнал не глубже пяти страниц
const MAX_PAGES = 5;
// Сервер принимает период до 3660 дней
const HISTORY_DAYS = 3650;

// Последние начисления в буфер компании на «Обзоре» (прототип, блок «Начисления»).
// Срок кабинета по ним не считаем: его считает бэк.
export default {
  name: 'ClientAccruals',
  components: { StateBlock },
  props: {
    corpId: { type: [Number, String], required: true },
    // Показывать ссылку на вкладку «История операций»
    canOpenOperations: { type: Boolean, default: false },
  },
  data: () => ({
    items: [],
    loading: false,
    error: false,
    // Нет доступа к журналу: блок не показываем
    forbidden: false,
    // Просмотрели лимит страниц, а начислений не нашли
    limitReached: false,
    requestId: 0,
  }),
  watch: {
    corpId() {
      this.items = [];
      this.load();
    },
  },
  methods: {
    formatDate(value) {
      return value ? moment(value).format('DD.MM.YYYY') : '—';
    },
    async load() {
      this.requestId += 1;
      const { requestId } = this;
      this.loading = true;
      this.error = false;
      this.forbidden = false;
      this.limitReached = false;
      try {
        const params = {
          from_date: moment().subtract(HISTORY_DAYS, 'days').format(FORMAT),
          to_date: moment().format(FORMAT),
          page_size: PAGE_SIZE,
        };
        const found = [];
        let reachedEnd = false;
        for (let page = 1; page <= MAX_PAGES && found.length < SHOW; page += 1) {
          // Страницы идут последовательно: следующая нужна, только если на этой начислений не хватило
          // eslint-disable-next-line no-await-in-loop
          const raw = await api.get(`/users/corp_clients/${this.corpId}/wallet-transactions/`, { params: { ...params, page } });
          if (requestId !== this.requestId) return;
          const { items, count } = parseWalletTransactions(raw);
          found.push(...items.filter((o) => o.toBuffer && o.qty > 0));
          if (page * PAGE_SIZE >= count) {
            reachedEnd = true;
            break;
          }
        }
        this.items = found.slice(0, SHOW);
        this.limitReached = !this.items.length && !reachedEnd;
      } catch (e) {
        if (requestId !== this.requestId) return;
        this.items = [];
        if (e && e.status === 403) this.forbidden = true;
        else this.error = true;
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped lang="scss">
.accruals-block {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;

  &__title {
    font-size: 16px;
    font-weight: 600;
  }
}
</style>
